/**
 * Global 域大型应用配置。
 *
 * @canonical 5.3-domain-owned-types
 * @来源 复制自 `constants/sideMenus.ts` 与 `constants/operationLogParams.ts`。
 * @删除条件 阶段二引用切到 `@dbl/core/global` 后，旧 constants 文件由人工删除。
 */

import type { MenuDefaultType } from '@/types/global';
import { DATALAB_EDITOR } from './constants';

// 所有页面的公共基础参数(openId参数每次请求时会动态去获取，然后再加入)
export const BASE_PARAMS = {
  logType: 'pageLog',
  pageLog: {
    source: 0,
    extInfo: '',
    typeId: 'datalab_pc_v2',
    isOnline: 'logIn',
    uidType: '',
    uid: '',
  },
};
// Pipeline-Overview页
export const PIPELINE_OVERVIEW = {
  pageId: '510001',
  pageName: 'pipelineOverview',
  pagePath: '/globalpedia/Pipeline/Overview',
};

export const MENU_CODE_WORK = 'workbench';

export const SIDE_MENU_CODE_MAP: Record<string, MenuDefaultType> = {
  [MENU_CODE_WORK]: {
    title: '工作台',
    icon: 'menu-customized-report',
  },
};
// 修改 MENU_LIST的同时, 也要修改 MENU_URLS
export const MENU_LIST = [
  {
    name_en: 'Home',
    name_zh: '主页',
    id: '01',
    parent_id: '0',
    icon: 'home-page',
    path: 'Home',
    page_id: 'datalab_home',
    env: ['pre', 'pro'],
  },
  // {
  //   name_en: 'Chat BI',
  //   name_zh: 'Chat BI',
  //   id: '21',
  //   parent_id: '2',
  //   icon: 'information-cue',
  //   path: 'Action/GptAnalysis',
  //   page_id: 'action_gpt_analysis',
  //   env: ['pre', 'pro'],
  // },
  {
    name_en: 'Workbench',
    name_zh: '工作台',
    icon: 'global-icon',
    id: '1',
    page_id: MENU_CODE_WORK,
    defaultOpened: true,
    env: ['pre', 'pro'],
    subMenus: [
      {
        name_en: 'Dashboard Center',
        name_zh: '报表中心',
        id: '12',
        parent_id: '1',
        path: 'WorkBench/MyDashboard',
        page_id: 'my_dashboard',
        privileges: [DATALAB_EDITOR.Dashbord],
        env: ['pre', 'pro'],
      },
      {
        name_en: 'Chart Center',
        name_zh: '图表中心',
        id: '11',
        parent_id: '1',
        path: 'WorkBench/MyAnalysis',
        page_id: 'my_analysis',
        privileges: [DATALAB_EDITOR.Chart],
        env: ['pre', 'pro'],
      },
    ],
  },
  {
    name_en: 'Self-Service Analysis',
    name_zh: '自助分析',
    icon: 'camera',
    id: '2',
    page_id: 'analysis',
    defaultOpened: true,
    env: ['pre', 'pro'],
    subMenus: [
      {
        name_en: 'Exploration Analysis',
        name_zh: '探索分析',
        id: '22',
        parent_id: '2',
        path: 'Action/ExploreAnalysis',
        page_id: 'action_explore_analysis',
        privileges: [DATALAB_EDITOR.Chart],
        env: ['pre', 'pro'],
      },
      {
        name_en: 'Drag & Drop',
        name_zh: '拖拉拽分析',
        id: '23',
        parent_id: '2',
        path: 'Analysis/MultiAnalysis',
        page_id: 'multi_dimensional_analysis',
        privileges: [DATALAB_EDITOR.AddChartBtn],
        env: ['pre', 'pro'],
      },
      {
        name_en: 'A/B Test Analysis',
        name_zh: 'A/B实验分析',
        id: '25',
        parent_id: '2',
        path: 'Analysis/ABTest',
        page_id: 'ab_test',
        privileges: [DATALAB_EDITOR.AddChartBtn],
        env: ['pre', 'pro'],
      },
      {
        name_en: 'User Extraction',
        name_zh: '号码包提取',
        id: '24',
        parent_id: '2',
        path: 'Analysis/PacketExtraction',
        page_id: 'packet_extraction',
        privileges: [DATALAB_EDITOR.AddChartBtn],
        env: ['pre', 'pro'],
      },
    ],
  },
  {
    name_en: 'Data Management',
    name_zh: '数据管理',
    icon: 'trash-can',
    id: '3',
    page_id: 'data_management',
    defaultOpened: true,
    env: ['pre', 'pro'],
    subMenus: [
      {
        name_en: 'Table',
        name_zh: '物理表',
        id: '31',
        parent_id: '3',
        path: 'DataManagement/SheetManagement',
        page_id: 'data_sheet_management',
        privileges: [DATALAB_EDITOR.Chart, DATALAB_EDITOR.Dataset],
        env: ['pre', 'pro'],
      },
      {
        name_en: 'SQL View',
        name_zh: 'SQL视图',
        id: '33',
        parent_id: '3',
        path: 'DataManagement/SqlView',
        page_id: 'data_sql_view',
        privileges: [DATALAB_EDITOR.Chart, DATALAB_EDITOR.Dataset],
        env: ['pre', 'pro'],
      },
      {
        name_en: 'User ID',
        name_zh: '号码包',
        id: '32',
        parent_id: '3',
        path: 'DataManagement/UserPackagesManagement',
        page_id: 'user_packages_management',
        privileges: [DATALAB_EDITOR.Chart],
        env: ['pre', 'pro'],
      },
      {
        name_en: 'Personal Data',
        name_zh: '个人数据',
        id: '34',
        parent_id: '3',
        path: 'DataManagement/PersonalData',
        page_id: 'personal_data',
        showNew: true,
        newMarkType: 2,
        privileges: [DATALAB_EDITOR.Chart, DATALAB_EDITOR.Dataset],
        env: ['pre', 'pro'],
      },
      {
        name_en: 'Model Management Center',
        name_zh: '模型管理中心',
        id: '35',
        parent_id: '3',
        path: 'DataManagement/ModelManagementCenter',
        page_id: 'data_model_management',
        privileges: [DATALAB_EDITOR.Chart, DATALAB_EDITOR.Dataset],
        env: ['pre', 'pro'],
      },
    ],
  },
  // 修改 MENU_LIST的同时, 也要修改 MENU_URLS
];

export const MENU_URLS = [
  'Home',
  'Action/GptAnalysis',
  'WorkBench/MyDashboard',
  'WorkBench/MyAnalysis',
  'Action/ExploreAnalysis',
  'Analysis/MultiAnalysis',
  'Analysis/PacketExtraction',
  'Analysis/ABTest',
  'DataManagement/SheetManagement',
  'DataManagement/SheetManagement/Detail',
  'DataManagement/SheetManagement/Edit',
  'DataManagement/UserPackagesManagement',
  'DataManagement/SqlView',
  'DataManagement/PersonalData',
  'DataManagement/ModelManagementCenter',
  'DataManagement/ModelManagementCenter/Edit',
  'DataManagement/ModelManagementCenter/Create',
  'WorkBench/MyDashboard/CreateDashboard',
  'WorkBench/MyDashboard/ViewPage',
];
// 下钻页路径配置
export const DRILL_DOWN_PAGES: { [key: string]: string } = {
  '/WorkBench/MyDashboard/CreateDashboard': 'WorkBench/MyDashboard',
  '/WorkBench/MyDashboard/ViewPage': 'WorkBench/MyDashboard',
  '/DataManagement/SheetManagement/Detail': 'DataManagement/SheetManagement',
  '/DataManagement/SheetManagement/Edit': 'DataManagement/SheetManagement',
  '/DataManagement/ModelManagementCenter/Edit':
    'DataManagement/ModelManagementCenter',
  '/DataManagement/ModelManagementCenter/Create':
    'DataManagement/ModelManagementCenter',
};

export const DEFAULT_WIDTH = 256;
export const MAX_WIDTH = 400;
export const GAME_LIST_WIDTH = 460;

export const PAGE_INFO_INIT = { pageSize: 50, pageIndex: 1 };

export const JUMP_TO_HOME_PATH_ARR = ['WorkBench/MyDashboard/CreateDashboard'];
export const DATALAB_HOME_PATH = 'Action/GptAnalysis';

// 根据以往 datalab 的前端维度翻译得到下面这些可能的维度字段。
export const COMMON_DIMENSION = [
  'login_channelid',
  'channelid',
  'channel',
  'country_code',
  'country',
  'lang_type',
  'lang',
  'os',
  'zone_id',
  'zone',
  '渠道',
  '国家',
  '系统',
  '区服',
  '区域',
  '对比项',
];

export const DASHBOARD_PATH_ARR = [
  'WorkBench/MyDashboard/CreateDashboard',
  'WorkBench/MyDashboard',
  'WorkBench/MyDashboard/ViewPage',
];
